import { format } from 'date-fns';
import { api } from '../../../js/api/core/request';

/**
 * Everything a download file name may not carry: punctuation and symbols (commas,
 * periods, quotes, slashes...). Letters, digits, spaces, hyphens and underscores stay.
 * The backend applies the same rule (`documents.utils.download_filename_stem`).
 */
const FILENAME_PUNCTUATION = /[^\p{L}\p{N}\s_-]/gu;

/** `name` as the stem of a file handed to a user: no punctuation, single spaces. */
export const downloadFilenameStem = (name: string): string =>
  name.replace(FILENAME_PUNCTUATION, '').split(/\s+/).filter(Boolean).join(' ');

/**
 * Name for a zip of documents: who and what they are, then when it was made —
 * "John Joe M.D. Education Documents" → "John Joe MD Education Documents 20261001-1532".
 * Callers put the first name first (no comma is left to mark an inverted name).
 */
export const documentsZipName = (subject: string, now: Date = new Date()): string =>
  `${downloadFilenameStem(subject)} ${format(now, 'yyyyMMdd-HHmm')}`;

export type DocumentsZipResult = { included: number; skipped: number };

/**
 * Download several documents as one zip (`POST /api/documents/zip/`) and save it under
 * `filename`. The server packs only the documents this user may download and reports
 * how many it left out; nothing downloadable rejects with a 404.
 */
export async function downloadDocumentsZip({
  ids,
  filename,
}: {
  ids: string[];
  filename: string;
}): Promise<DocumentsZipResult> {
  const response = await api.post<Blob>(
    '/api/documents/zip/',
    { ids, filename },
    { responseType: 'blob' }
  );
  const url = window.URL.createObjectURL(response.data);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', `${filename}.zip`);
  document.body.appendChild(link);
  link.click();
  link.parentNode?.removeChild(link);
  window.URL.revokeObjectURL(url);
  return {
    included: Number(response.headers['x-documents-included'] ?? ids.length),
    skipped: Number(response.headers['x-documents-skipped'] ?? 0),
  };
}
